import React, { useState } from "react";
import { Card, Form, Row, Col, Button, Badge, Alert } from "react-bootstrap";

const DIAMOND_SHAPES = [
  "Princess",
  "Round",
  "Heart",
  "Oval",
  "Cushion",
  "Pear",
  "Emerald",
  "Asscher",
  "Marquise",
];

const GEMSTONE_TYPES = [
  "Ruby",
  "Emerald",
  "Blue Sapphire",
  "Yellow Sapphire",
  "Amethyst",
  "Golden Pearl",
  "White Pearl",
  "Catseye",
  "Coral",
  "Garnet",
  "Opal",
];

const GEMSTONE_SHAPES = [
  "Round",
  "Princess",
  "Emerald",
  "Cushion",
  "Oval",
  "Marquise",
  "Pear",
];

export default function JewelleryFormInline({ onGenerate, loading }) {
  const [form, setForm] = useState({
    ornament: "Ring",
    width: "",
    length: "",

    goldWeight: "",
    goldColour: "Yellow",
    goldPurity: "18K",

    custom: "",
  });

  const [referenceImage, setReferenceImage] = useState(null);

  const [preview, setPreview] = useState("");

  const [diamonds, setDiamonds] = useState([]);

  const [stones, setStones] = useState([]);

  const [error, setError] = useState("");

  // -----------------------------
  // STATIC FORM CHANGE
  // -----------------------------
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // -----------------------------
  // IMAGE UPLOAD
  // -----------------------------
  const handleImageUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setReferenceImage(file);

    setPreview(URL.createObjectURL(file));
  };

  // -----------------------------
  // DIAMONDS
  // -----------------------------
  const addDiamond = () => {
    setDiamonds([
      ...diamonds,
      {
        shape: "Round",
        pieces: "",
        weight: "",
      },
    ]);
  };

  const updateDiamond = (index, field, value) => {
    const updated = [...diamonds];
    updated[index][field] = value;
    setDiamonds(updated);
  };

  const removeDiamond = (index) => {
    setDiamonds(diamonds.filter((_, i) => i !== index));
  };

  // -----------------------------
  // GEMSTONES
  // -----------------------------
  const addStone = () => {
    setStones([
      ...stones,
      {
        colour: "Ruby",
        shape: "Round",
        intensity: "Medium",
        pieces: "",
        weight: "",
      },
    ]);
  };

  const updateStone = (index, field, value) => {
    const updated = [...stones];
    updated[index][field] = value;
    setStones(updated);
  };

  const removeStone = (index) => {
    setStones(stones.filter((_, i) => i !== index));
  };

  // -----------------------------
  // BUILD PROMPT
  // -----------------------------
  const buildPrompt = () => {
    let prompt = "";

    // -----------------------------
    // IF REFERENCE IMAGE EXISTS
    // -----------------------------
    if (referenceImage) {
      prompt += `
Use the uploaded jewellery image as the primary reference.

Try to Preserve the jewellery structure,
shape, stone placements, craftsmanship,
proportions and luxury aesthetics while incorporating the 
further changes mentioned below. 
Give more weightage to additional instructions if it exits

`;
    }

    prompt += `Design a luxurious ${form.ornament}`;

    // Ornament size
    const dimensions = [];

    if (form.width) dimensions.push(`width ${form.width}`);

    if (form.length) dimensions.push(`length ${form.length}`);

    if (dimensions.length > 0) {
      prompt += ` with ${dimensions.join(", ")}`;
    }

    prompt += `.\n\n`;

    // GOLD
    const goldLines = [];

    if (form.goldWeight) goldLines.push(`- Weight: ${form.goldWeight} grams`);

    if (form.goldColour) goldLines.push(`- Colour: ${form.goldColour} gold`);

    if (form.goldPurity) goldLines.push(`- Purity: ${form.goldPurity}`);

    if (goldLines.length > 0) {
      prompt += `Gold:\n${goldLines.join("\n")}\n\n`;
    }

    // DIAMONDS
    if (diamonds.length > 0) {
      const validDiamonds = diamonds.filter(
        (d) => d.shape || d.pieces || d.weight,
      );

      if (validDiamonds.length > 0) {
        prompt += `Diamonds:\n`;

        validDiamonds.forEach((d, i) => {
          prompt += `Diamond Type ${i + 1}:\n`;

          if (d.shape) prompt += `- Shape: ${d.shape}\n`;

          if (d.pieces) prompt += `- Pieces: ${d.pieces}\n`;

          if (d.weight) prompt += `- Total Weight: ${d.weight} carats\n`;

          prompt += `\n`;
        });
      }
    }

    // GEMSTONES
    if (stones.length > 0) {
      const validStones = stones.filter(
        (s) => s.colour || s.shape || s.intensity || s.pieces || s.weight,
      );

      if (validStones.length > 0) {
        prompt += `Colour Stones:\n`;

        validStones.forEach((s, i) => {
          prompt += `Stone Type ${i + 1}:\n`;

          if (s.colour) prompt += `- Colour Stone: ${s.colour}\n`;

          if (s.shape) prompt += `- Shape: ${s.shape}\n`;

          if (s.intensity) prompt += `- Intensity: ${s.intensity}\n`;

          if (s.pieces) prompt += `- Pieces: ${s.pieces}\n`;

          if (s.weight) prompt += `- Total Weight: ${s.weight} carats\n`;

          prompt += `\n`;
        });
      }
    }

    // CUSTOM
    if (form.custom?.trim()) {
      prompt += `Additional Instructions:\n${form.custom}\n\n`;
    }

    // FINAL STYLE
    prompt += `
Create an ultra realistic luxury diamond jewelry render.
High-end product photography.
8K resolution.
Soft cinematic studio lighting.
Black velvet luxury background.
Photorealistic diamonds and gemstones.
Premium jewellery aesthetics.
Extremely detailed gold texture and reflections.
`;

    return prompt;
  };

  // -----------------------------
  // SUBMIT
  // -----------------------------
  const handleSubmit = () => {
    if (loading) return;

    setError("");

    const prompt = buildPrompt();

    onGenerate({
      prompt,
      referenceImage,
    });
  };

  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        <h5 className="mb-4">Jewellery Design Form</h5>

        {error && <Alert variant="danger">{error}</Alert>}

        {/* ===================================== */}
        {/* REFERENCE IMAGE */}
        {/* ===================================== */}

        <h6 style={{ color: "#082372" }} className="mb-3">
          Existing Jewellery Design (Optional)
        </h6>

        <Form.Group className="mb-4">
          <Form.Control
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
          />

          <div className="text-muted small mt-2">
            Upload an existing jewellery design to modify or enhance it using
            AI.
          </div>
        </Form.Group>

        {preview && (
          <div className="mb-4">
            <img
              src={preview}
              alt=""
              style={{
                width: 220,
                borderRadius: 10,
                border: "1px solid #ddd",
              }}
            />
          </div>
        )}

        {/* ORNAMENT */}
        <h6 style={{ color: "#082372" }} className="mb-3">
          Ornament
        </h6>

        <Row className="g-2 mb-4">
          <Col md={4}>
            <Form.Select
              name="ornament"
              value={form.ornament}
              onChange={handleChange}
            >
              <option>Ring</option>
              <option>Necklace</option>
              <option>Bracelet</option>
              <option>Bangle</option>
              <option>Earrings</option>
              <option>Pendant</option>
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Control
              name="width"
              value={form.width}
              onChange={handleChange}
              placeholder="Width"
            />
          </Col>

          <Col md={4}>
            <Form.Control
              name="length"
              value={form.length}
              onChange={handleChange}
              placeholder="Length"
            />
          </Col>
        </Row>

        {/* GOLD */}
        <h6 style={{ color: "#082372" }} className="mb-3">
          Gold
        </h6>

        <Row className="g-2 mb-4">
          <Col md={4}>
            <Form.Control
              name="goldWeight"
              value={form.goldWeight}
              onChange={handleChange}
              placeholder="Gold Weight"
            />
          </Col>

          <Col md={4}>
            <Form.Select
              name="goldColour"
              value={form.goldColour}
              onChange={handleChange}
            >
              <option>Yellow</option>
              <option>Rose</option>
              <option>White</option>
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Select
              name="goldPurity"
              value={form.goldPurity}
              onChange={handleChange}
            >
              <option>14K</option>
              <option>18K</option>
              <option>22K</option>
            </Form.Select>
          </Col>
        </Row>

        {/* DIAMONDS */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 style={{ color: "#082372" }} className="mb-0">
            Diamonds
          </h6>

          <Button size="sm" onClick={addDiamond}>
            + Add Diamond Type
          </Button>
        </div>

        {diamonds.map((diamond, index) => (
          <Card key={index} className="mb-3 border">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Badge bg="dark">Diamond #{index + 1}</Badge>

                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => removeDiamond(index)}
                >
                  Remove
                </Button>
              </div>

              <Row className="g-2">
                <Col md={4}>
                  <Form.Select
                    value={diamond.shape}
                    onChange={(e) =>
                      updateDiamond(index, "shape", e.target.value)
                    }
                  >
                    {DIAMOND_SHAPES.map((shape) => (
                      <option key={shape}>{shape}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Control
                    placeholder="Number of Pieces"
                    value={diamond.pieces}
                    onChange={(e) =>
                      updateDiamond(index, "pieces", e.target.value)
                    }
                  />
                </Col>

                <Col md={4}>
                  <Form.Control
                    placeholder="Total Weight"
                    value={diamond.weight}
                    onChange={(e) =>
                      updateDiamond(index, "weight", e.target.value)
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}

        {/* GEMSTONES */}
        <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
          <h6 style={{ color: "#082372" }} className="mb-0">
            Colour Stones
          </h6>

          <Button size="sm" onClick={addStone}>
            + Add Colour Stone
          </Button>
        </div>

        {stones.map((stone, index) => (
          <Card key={index} className="mb-3 border">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Badge bg="secondary">Stone #{index + 1}</Badge>

                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => removeStone(index)}
                >
                  Remove
                </Button>
              </div>

              <Row className="g-2">
                <Col md={4}>
                  <Form.Select
                    value={stone.colour}
                    onChange={(e) =>
                      updateStone(index, "colour", e.target.value)
                    }
                  >
                    {GEMSTONE_TYPES.map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Select
                    value={stone.shape}
                    onChange={(e) =>
                      updateStone(index, "shape", e.target.value)
                    }
                  >
                    {GEMSTONE_SHAPES.map((shape) => (
                      <option key={shape}>{shape}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Select
                    value={stone.intensity}
                    onChange={(e) =>
                      updateStone(index, "intensity", e.target.value)
                    }
                  >
                    <option>Light</option>
                    <option>Medium</option>
                    <option>Dark</option>
                  </Form.Select>
                </Col>

                <Col md={6}>
                  <Form.Control
                    placeholder="Number of Pieces"
                    value={stone.pieces}
                    onChange={(e) =>
                      updateStone(index, "pieces", e.target.value)
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Control
                    placeholder="Total Weight"
                    value={stone.weight}
                    onChange={(e) =>
                      updateStone(index, "weight", e.target.value)
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}

        {/* CUSTOM */}
        <h6 style={{ color: "#082372" }} className="mt-4 mb-2">
          Additional Instructions
        </h6>

        <Form.Control
          as="textarea"
          rows={3}
          name="custom"
          value={form.custom}
          onChange={handleChange}
          placeholder="Describe the style, inspiration, setting, detailing, etc."
        />

        {/* SUBMIT */}
        <Button
          className="w-100 mt-4"
          size="lg"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading
            ? "Generating..."
            : referenceImage
              ? "Generate From Existing Design"
              : "Generate Jewellery Design"}
        </Button>
      </Card.Body>
    </Card>
  );
}
